import React from "react";
import moment from "moment";
import { Icon, Tooltip, DatePicker, Popover, Button, Radio } from "antd";
import { Container, DateButton, InputWrapper, ModeToggle } from "./styles";
import styled from "styled-components";

var MonthPicker = DatePicker.MonthPicker;

var PresetList = styled.div`
    min-width: 200px;
`;

var PresetItem = styled.div`
    padding: 8px 12px;
    cursor: pointer;
    border-radius: 4px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 13px;
    color: ${function(props) { return props.active ? '#1890ff' : '#595959'; }};
    font-weight: ${function(props) { return props.active ? 600 : 400; }};
    background: ${function(props) { return props.active ? '#e6f7ff' : 'transparent'; }};

    &:hover {
        background: ${function(props) { return props.active ? '#e6f7ff' : '#f5f5f5'; }};
    }
`;

var PresetDivider = styled.div`
    height: 1px;
    background: #f0f0f0;
    margin: 4px 0;
`;

var PresetLabel = styled.div`
    padding: 4px 12px;
    font-size: 11px;
    color: #8c8c8c;
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

var CustomPickerWrapper = styled.div`
    padding: 4px 12px 8px;
`;

var RangeFieldLabel = styled.div`
    font-size: 11px;
    color: #8c8c8c;
    margin: 4px 0 2px;
`;

function formatRangeMonth(m) {
    var text = m.format('MMM/YYYY');
    return text.charAt(0).toUpperCase() + text.slice(1);
}

// Um intervalo "por mês" começa no dia 1 e termina no último dia do mês.
function isWholeMonths(start, end) {
    return start.date() === 1 && end.isSame(end.clone().endOf('month'), 'day');
}

function getPresets() {
    var now = moment();
    var presets = [];

    presets.push({ label: 'Este mês', value: now.clone().format('YYYY-MM') });
    presets.push({ label: 'Mês passado', value: now.clone().subtract(1, 'month').format('YYYY-MM') });

    for (var i = 2; i <= 5; i++) {
        var m = now.clone().subtract(i, 'months');
        var name = m.format('MMMM [de] YYYY');
        presets.push({ label: name.charAt(0).toUpperCase() + name.slice(1), value: m.format('YYYY-MM') });
    }

    return presets;
}

var DateNavigation = function(props) {
    var date = props.date;
    var onChange = props.onChange;
    var format = props.format || "YYYY-MM";
    var mode = props.mode || "month";
    var onModeChange = props.onModeChange;
    // Intervalo (opcional): rangeFrom/rangeTo em "YYYY-MM-DD" e onRangeChange(from, to), também em "YYYY-MM-DD".
    // O "Personalizado" deixa escolher por mês (De/Até mês) ou por data (De/Até dia).
    // Sem onRangeChange, o "Personalizado" continua escolhendo um mês só.
    var onRangeChange = props.onRangeChange;

    var isDaily = mode === "day";
    var isYearly = mode === "year";
    var currentFormat = isDaily ? "YYYY-MM-DD" : "YYYY-MM";
    var currentDate = moment(date, currentFormat);

    var allowRange = !!onRangeChange && !isDaily && !isYearly;
    var rangeStart = allowRange && props.rangeFrom ? moment(props.rangeFrom, "YYYY-MM-DD") : null;
    var rangeEnd = allowRange && props.rangeTo ? moment(props.rangeTo, "YYYY-MM-DD") : null;
    var hasRange = !!(rangeStart && rangeEnd);
    var rangeByMonth = hasRange && isWholeMonths(rangeStart, rangeEnd);
    // Um mês inteiro sozinho é o modo normal (atalhos e setas de mês); qualquer outra coisa é intervalo.
    var isRange = hasRange && !(rangeByMonth && rangeStart.isSame(rangeEnd, 'month'));
    var rangeMonths = rangeByMonth ? rangeEnd.diff(rangeStart, 'months') + 1 : 0;
    var rangeDays = hasRange ? rangeEnd.diff(rangeStart, 'days') + 1 : 0;

    var popoverState = React.useState(false);
    var popoverVisible = popoverState[0];
    var setPopoverVisible = popoverState[1];

    var customState = React.useState(false);
    var showCustom = customState[0];
    var setShowCustom = customState[1];

    var draftFromState = React.useState(null);
    var draftFrom = draftFromState[0];
    var setDraftFrom = draftFromState[1];

    var draftToState = React.useState(null);
    var draftTo = draftToState[0];
    var setDraftTo = draftToState[1];

    var draftUnitState = React.useState('month');
    var draftUnit = draftUnitState[0];
    var setDraftUnit = draftUnitState[1];

    var openCustom = function() {
        if (isRange) {
            setDraftUnit(rangeByMonth ? 'month' : 'day');
            setDraftFrom(rangeStart.clone());
            setDraftTo(rangeEnd.clone());
        } else {
            setDraftUnit('month');
            setDraftFrom(currentDate.clone().startOf('month'));
            setDraftTo(currentDate.clone().endOf('month'));
        }
        setShowCustom(true);
    };

    var changeDraftUnit = function(unit) {
        setDraftUnit(unit);
        if (unit === 'month') {
            if (draftFrom) setDraftFrom(draftFrom.clone().startOf('month'));
            if (draftTo) setDraftTo(draftTo.clone().endOf('month'));
        }
    };

    var applyRange = function() {
        if (!draftFrom || !draftTo) return;
        var unit = draftUnit === 'month' ? 'month' : 'day';
        var swap = draftFrom.isAfter(draftTo, unit);
        var from = (swap ? draftTo : draftFrom).clone().startOf(unit);
        var to = (swap ? draftFrom : draftTo).clone().endOf(unit);
        onRangeChange(from.format("YYYY-MM-DD"), to.format("YYYY-MM-DD"));
        setPopoverVisible(false);
        setShowCustom(false);
    };

    // As setas andam pelo tamanho do intervalo: jan–jun → jul–dez; 01/03–15/03 → 16/03–30/03.
    var shiftRange = function(direction) {
        if (rangeByMonth) {
            onRangeChange(
                rangeStart.clone().add(direction * rangeMonths, 'months').startOf('month').format("YYYY-MM-DD"),
                rangeEnd.clone().add(direction * rangeMonths, 'months').endOf('month').format("YYYY-MM-DD")
            );
        } else {
            onRangeChange(
                rangeStart.clone().add(direction * rangeDays, 'days').format("YYYY-MM-DD"),
                rangeEnd.clone().add(direction * rangeDays, 'days').format("YYYY-MM-DD")
            );
        }
    };

    var handlePrevious = function() {
        if (isRange) {
            shiftRange(-1);
            return;
        }
        var unit = isYearly ? 'year' : (isDaily ? 'day' : 'month');
        onChange(currentDate.clone().subtract(1, unit).format(currentFormat));
    };

    var handleNext = function() {
        if (isRange) {
            shiftRange(1);
            return;
        }
        var unit = isYearly ? 'year' : (isDaily ? 'day' : 'month');
        onChange(currentDate.clone().add(1, unit).format(currentFormat));
    };

    var handleModeToggle = function() {
        if (onModeChange) {
            var newMode = isDaily ? "month" : "day";
            onModeChange(newMode);
            if (newMode === "day") {
                onChange(moment().format("YYYY-MM-DD"));
            } else {
                onChange(currentDate.clone().format("YYYY-MM"));
            }
        }
    };

    var handleDateChange = function(selectedDate) {
        if (selectedDate) {
            onChange(selectedDate.format(currentFormat));
            setPopoverVisible(false);
            setShowCustom(false);
        }
    };

    var handlePresetClick = function(value) {
        if (isDaily) {
            onChange(value);
        } else {
            onChange(value);
        }
        setPopoverVisible(false);
        setShowCustom(false);
    };

    var presets = isYearly ? (function() {
        var now = moment();
        var yp = [];
        for (var yi = 0; yi <= 3; yi++) {
            var yr = now.clone().subtract(yi, 'years');
            yp.push({ label: yr.format('YYYY'), value: yr.format('YYYY-MM') });
        }
        return yp;
    })() : getPresets();
    var currentValue = isYearly ? currentDate.format('YYYY') : currentDate.format('YYYY-MM');

    var popoverContent = (
        <PresetList>
            <PresetLabel>Atalhos</PresetLabel>
            {presets.map(function(preset) {
                var isPresetActive = isRange ? false : isYearly
                    ? currentDate.format('YYYY') === moment(preset.value, 'YYYY-MM').format('YYYY')
                    : currentValue === preset.value;
                return (
                    <PresetItem
                        key={preset.value}
                        active={isPresetActive}
                        onClick={function() { handlePresetClick(preset.value); }}
                    >
                        <span>{preset.label}</span>
                        {isPresetActive && <Icon type="check" style={{ fontSize: 12 }} />}
                    </PresetItem>
                );
            })}
            <PresetDivider />
            {!showCustom && (
                <PresetItem active={isRange} onClick={openCustom}>
                    <span><Icon type="calendar" style={{ marginRight: 6 }} />Personalizado</span>
                    {isRange
                        ? <Icon type="check" style={{ fontSize: 12 }} />
                        : <Icon type="right" style={{ fontSize: 10, opacity: 0.5 }} />}
                </PresetItem>
            )}
            {showCustom && (
                <CustomPickerWrapper>
                    {allowRange ? (
                        <div>
                            <Radio.Group
                                size="small"
                                value={draftUnit}
                                onChange={function(e) { changeDraftUnit(e.target.value); }}
                                style={{ display: 'flex', marginBottom: 4 }}
                            >
                                <Radio.Button value="month" style={{ flex: 1, textAlign: 'center' }}>Por mês</Radio.Button>
                                <Radio.Button value="day" style={{ flex: 1, textAlign: 'center' }}>Por data</Radio.Button>
                            </Radio.Group>
                            <RangeFieldLabel>De</RangeFieldLabel>
                            {draftUnit === 'month' ? (
                                <MonthPicker
                                    key="from-month"
                                    value={draftFrom}
                                    onChange={function(v) { setDraftFrom(v ? v.clone().startOf('month') : v); }}
                                    allowClear={false}
                                    format="MMM/YYYY"
                                    style={{ width: '100%' }}
                                    getCalendarContainer={function(trigger) { return trigger.parentNode; }}
                                />
                            ) : (
                                <DatePicker
                                    key="from-day"
                                    value={draftFrom}
                                    onChange={setDraftFrom}
                                    allowClear={false}
                                    format="DD/MM/YYYY"
                                    style={{ width: '100%' }}
                                    getCalendarContainer={function(trigger) { return trigger.parentNode; }}
                                />
                            )}
                            <RangeFieldLabel>Até</RangeFieldLabel>
                            {draftUnit === 'month' ? (
                                <MonthPicker
                                    key="to-month"
                                    value={draftTo}
                                    onChange={function(v) { setDraftTo(v ? v.clone().endOf('month') : v); }}
                                    allowClear={false}
                                    format="MMM/YYYY"
                                    style={{ width: '100%' }}
                                    getCalendarContainer={function(trigger) { return trigger.parentNode; }}
                                />
                            ) : (
                                <DatePicker
                                    key="to-day"
                                    value={draftTo}
                                    onChange={setDraftTo}
                                    allowClear={false}
                                    format="DD/MM/YYYY"
                                    style={{ width: '100%' }}
                                    getCalendarContainer={function(trigger) { return trigger.parentNode; }}
                                />
                            )}
                            <Button
                                type="primary"
                                size="small"
                                block
                                style={{ marginTop: 10 }}
                                disabled={!draftFrom || !draftTo}
                                onClick={applyRange}
                            >
                                Aplicar
                            </Button>
                        </div>
                    ) : isDaily ? (
                        <DatePicker
                            value={currentDate}
                            onChange={handleDateChange}
                            open={true}
                            style={{ width: '100%' }}
                            getCalendarContainer={function(trigger) { return trigger.parentNode; }}
                        />
                    ) : (
                        <MonthPicker
                            value={currentDate}
                            onChange={handleDateChange}
                            open={true}
                            style={{ width: '100%' }}
                            getCalendarContainer={function(trigger) { return trigger.parentNode; }}
                        />
                    )}
                </CustomPickerWrapper>
            )}
        </PresetList>
    );

    var displayFormat = isYearly ? "YYYY" : (isDaily ? "DD [de] MMMM [de] YYYY" : "MMMM [de] YYYY");
    var displayText = currentDate.format(displayFormat);
    displayText = displayText.charAt(0).toUpperCase() + displayText.slice(1);
    if (isRange) {
        displayText = rangeByMonth
            ? formatRangeMonth(rangeStart) + ' – ' + formatRangeMonth(rangeEnd)
            : rangeStart.format('DD/MM/YYYY') + ' – ' + rangeEnd.format('DD/MM/YYYY');
    }
    var previousLabel = isRange ? "Período anterior" : (isDaily ? "Dia anterior" : "Mês anterior");
    var nextLabel = isRange ? "Próximo período" : (isDaily ? "Próximo dia" : "Próximo mês");

    return (
        <Container>
            <Tooltip title={previousLabel}>
                <DateButton onClick={handlePrevious}>
                    <Icon type="left" />
                </DateButton>
            </Tooltip>

            <Popover
                content={popoverContent}
                trigger="click"
                placement="bottom"
                visible={popoverVisible}
                onVisibleChange={function(v) { setPopoverVisible(v); if (!v) setShowCustom(false); }}
            >
                <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '0 8px', height: 28, borderRadius: 4 }}>
                    <Icon type="calendar" style={{ color: '#8c8c8c', fontSize: 14, marginRight: 6 }} />
                    <span style={{ fontSize: 14, color: '#262626', textTransform: isRange ? 'none' : 'capitalize', whiteSpace: 'nowrap' }}>
                        {displayText}
                    </span>
                </div>
            </Popover>

            <Tooltip title={nextLabel}>
                <DateButton onClick={handleNext}>
                    <Icon type="right" />
                </DateButton>
            </Tooltip>

            {onModeChange && (
                <Tooltip title={isDaily ? "Ver por mês" : "Ver por dia"}>
                    <ModeToggle onClick={handleModeToggle}>
                        <Icon type={isDaily ? "calendar" : "schedule"} />
                    </ModeToggle>
                </Tooltip>
            )}
        </Container>
    );
};

export default DateNavigation;
